import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Prompt, Redirect } from 'react-router-dom';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Loading from '../ui/Loading.js';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Utils from '../../commons/utils/Utils';
import Security from '../../commons/security/Security.js';
import { tarifariosHabilitado } from './InventarioTarifariosGrid.jsx';
import InventarioTarifariosItems from './InventarioTarifariosItems.jsx';

const BASE_URL = '/inventario/tarifarios';

const NOMBRE_MAX = 100;
const DESCRIPCION_MAX = 255;

const FORM_INIT = {
  id: 0,
  nombre: '',
  descripcion: '',
  proveedores: [],
};

/** Vacío = usa el del Catálogo y viaja como null, nunca como '' ni como 0. El 0 es un monto válido. */
const aMonto = (valor) =>
  valor === null || valor === undefined || String(valor).trim() === '' ? null : Number(valor);

/** Mismo criterio que InventarioTarifariosGridRow: los nombres van al `html` de SweetAlert2. */
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * VEC-3499 — ABM de un tarifario: Nombre, Descripción y los proveedores que lo usan.
 *
 * Un proveedor tiene un solo tarifario: si se elige uno que ya tiene otro, al guardar se avisa y se
 * pide confirmación antes de reasignarlo.
 *
 * La sección "Ítems del Catálogo" (solo en edición y consulta: el import necesita el tarifario
 * creado) acumula las ediciones en `cambiosItems` y las manda con el mismo Guardar, así nombre, proveedores e ítems se graban en una sola transacción. Si se sale con
 * cambios sin guardar, se pide confirmación.
 */
const InventarioTarifariosAbm = (props) => {
  const intl = useIntl();
  const [redirectTo, setRedirectTo] = useState(null);
  const [formData, setFormData] = useState({ ...FORM_INIT });
  const [loading, setLoading] = useState(false);
  const [opcionesProveedores, setOpcionesProveedores] = useState([]);
  // itemId => { costo, valorMaximo, costoFijo }: estado completo de cada fila que se tocó.
  const [cambiosItems, setCambiosItems] = useState({});
  const [guardado, setGuardado] = useState(false);

  const isView = props.action === 'VIEW';

  useEffect(() => {
    if (
      ((Security.hasPermission('TARIFARIOS_CREAR') && props.action === 'ADD') ||
        (Security.hasPermission('TARIFARIOS_MODIFICAR') && props.action === 'EDIT') ||
        (Security.hasPermission('TARIFARIOS_VISUALIZAR') && props.action === 'VIEW')) &&
      tarifariosHabilitado()
    ) {
      init();
    } else {
      setRedirectTo('/error');
    }
  }, [props.action, props.match && props.match.params && props.match.params.id]);

  /**
   * El combo trae los proveedores ACTIVOS con el tarifario que tiene cada uno. Los ya asociados que
   * se dieron de baja después no vienen en el combo, así que se agregan desde el tarifario: si no,
   * el Autocomplete no los podría mostrar como elegidos y guardar los sacaría sin que el usuario
   * lo haya pedido.
   */
  const init = () => {
    setLoading(true);
    const pedidoProveedores = isView ? Promise.resolve([]) : Utils.getData(`${BASE_URL}/proveedores/select`);
    const pedidoTarifario =
      props.action === 'ADD' ? Promise.resolve(null) : Utils.getData(`${BASE_URL}/${props.match.params.id}`);

    Promise.all([pedidoProveedores, pedidoTarifario])
      .then(([proveedores, tarifario]) => {
        let opciones = proveedores || [];
        if (tarifario) {
          const elegidos = (tarifario.proveedores || []).map((p) => {
            const opcion = opciones.find((o) => o.value === p.id);
            return opcion || { value: p.id, label: p.razonSocial, tarifarioId: tarifario.id, tarifarioNombre: tarifario.nombre };
          });
          opciones = opciones.concat(elegidos.filter((e) => !opciones.some((o) => o.value === e.value)));
          setFormData({
            id: tarifario.id,
            nombre: tarifario.nombre || '',
            descripcion: tarifario.descripcion || '',
            proveedores: elegidos,
          });
        } else if (props.action !== 'ADD') {
          exit();
        }
        setOpcionesProveedores(opciones);
      })
      .catch(() => exit())
      .finally(() => setLoading(false));
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCambioItem = (itemId, estado) => {
    setCambiosItems((prev) => ({ ...prev, [itemId]: estado }));
  };

  const hayCambiosItems = Object.keys(cambiosItems).length > 0;

  const warn = (message) => {
    Swal.fire({ icon: 'warning', title: message, confirmButtonText: 'OK' });
  };

  const validate = () => {
    const nombre = formData.nombre.trim();
    if (!nombre) {
      warn(intl.formatMessage({ id: 'inventarioTarifarios.error.nombre_required', defaultMessage: 'El nombre es requerido' }));
      return false;
    }
    if (nombre.length > NOMBRE_MAX) {
      warn(
        intl.formatMessage(
          { id: 'inventarioTarifarios.error.nombre_largo', defaultMessage: 'El nombre no puede superar los {max} caracteres' },
          { max: NOMBRE_MAX }
        )
      );
      return false;
    }
    const montoInvalido = Object.keys(cambiosItems).some((itemId) =>
        [cambiosItems[itemId].costo, cambiosItems[itemId].valorMaximo].some((v) => {
          const monto = aMonto(v);
          return monto !== null && (Number.isNaN(monto) || monto < 0);
        })
      );
    if (montoInvalido) {
      warn(
        intl.formatMessage({
          id: 'inventarioTarifarios.error.monto_invalido',
          defaultMessage: 'Los montos de los ítems deben ser números mayores o iguales a 0.',
        })
      );
      return false;
    }
    if (formData.descripcion.trim().length > DESCRIPCION_MAX) {
      warn(
        intl.formatMessage(
          { id: 'inventarioTarifarios.error.descripcion_larga', defaultMessage: 'La descripción no puede superar los {max} caracteres' },
          { max: DESCRIPCION_MAX }
        )
      );
      return false;
    }
    return true;
  };

  /** Proveedores elegidos que hoy usan OTRO tarifario: guardar los reasigna a este. */
  const proveedoresAReasignar = () =>
    formData.proveedores.filter((p) => p.tarifarioId && p.tarifarioId !== formData.id);

  const confirmarReasignacion = () => {
    const aReasignar = proveedoresAReasignar();
    if (aReasignar.length === 0) return Promise.resolve(true);

    const nombreNuevo = escapeHtml(formData.nombre.trim());
    const detalle = aReasignar
      .map((p) =>
        intl.formatMessage(
          {
            id: 'inventarioTarifarios.reasignar.detalle',
            defaultMessage: 'El proveedor {proveedor} ya tiene asociado el tarifario {anterior}. Pasará a utilizar {nuevo}.',
          },
          {
            proveedor: '<b>' + escapeHtml(p.label) + '</b>',
            anterior: '<b>' + escapeHtml(p.tarifarioNombre || '') + '</b>',
            nuevo: '<b>' + nombreNuevo + '</b>',
          }
        )
      )
      .join('<br/>');

    return Swal.fire({
      title: intl.formatMessage({
        id: 'inventarioTarifarios.reasignar.titulo',
        defaultMessage: '¿Confirma el cambio de tarifario?',
      }),
      html: detalle,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0ABFBC',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'Aceptar', defaultMessage: 'Aceptar' }),
      cancelButtonText: intl.formatMessage({ id: 'Cancelar', defaultMessage: 'Cancelar' }),
    }).then((result) => !!result.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    confirmarReasignacion().then((confirmado) => {
      if (!confirmado) return;

      setLoading(true);
      Utils.sendData(
        `${BASE_URL}${props.action !== 'ADD' ? '/' + formData.id : ''}`,
        props.action === 'ADD' ? 'POST' : 'PUT',
        {
          nombre: formData.nombre.trim(),
          descripcion: formData.descripcion.trim() !== '' ? formData.descripcion.trim() : null,
          proveedores: formData.proveedores.map((p) => p.value),
          ...(props.action === 'ADD'
            ? {}
            : {
                items: Object.keys(cambiosItems).map((itemId) => ({
                    itemId: Number(itemId),
                    costo: aMonto(cambiosItems[itemId].costo),
                    valorMaximo: aMonto(cambiosItems[itemId].valorMaximo),
                    costoFijo: !!cambiosItems[itemId].costoFijo,
                  })),
              }),
        }
      )
        .then((response) => {
          if (response) {
            // Sin esto el Prompt de cambios sin guardar frenaría la salida después de grabar.
            setGuardado(true);
            exit();
          }
        })
        // Utils.sendData ya muestra el error por Swal; acá solo se corta la propagación.
        .catch(() => {})
        .finally(() => setLoading(false));
    });
  };

  const exit = () => {
    setRedirectTo(BASE_URL);
  };

  const renderText = (name, label, required, maxLength) => (
    <div className="col-md-6">
      <div className="form-group row">
        <label className="col-md-4 label-control col-form-label" htmlFor={name}>
          {label}
          {required ? '*' : ''}:
        </label>
        <div className="col-md-8">
          {isView ? (
            <div className="form-control-static col-form-label form-value">{formData[name] !== '' ? formData[name] : '-'}</div>
          ) : (
            <input
              type="text"
              className="form-control"
              id={name}
              name={name}
              maxLength={maxLength}
              value={formData[name]}
              onChange={(e) => handleInputChange(name, e.target.value)}
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <Prompt
        when={hayCambiosItems && !guardado && !isView}
        message={intl.formatMessage({
          id: 'inventarioTarifarios.abm.cambios_sin_guardar',
          defaultMessage: 'Hay cambios en los ítems sin guardar. ¿Desea salir igual?',
        })}
      />
      {redirectTo && <Redirect push to={redirectTo} />}
      {loading && <Loading />}
      <div className="row">
        <div className="col-md-12">
          <form className="form form-horizontal">
            <div className="form-body">
              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="icon-home"></i>
                      {intl.formatMessage({ id: 'inventarioTarifarios.abm.header_datos_generales', defaultMessage: 'Datos Generales' })}
                      <div className="float-right" style={{ fontSize: '14px' }}>
                        * {intl.formatMessage({ id: 'inventarioTarifarios.abm.campos_requeridos', defaultMessage: 'campos requeridos' })}
                      </div>
                    </h4>
                    <div className="row">
                      {renderText(
                        'nombre',
                        intl.formatMessage({ id: 'inventarioTarifarios.abm.nombre', defaultMessage: 'Nombre' }),
                        true,
                        NOMBRE_MAX
                      )}
                      {renderText(
                        'descripcion',
                        intl.formatMessage({ id: 'inventarioTarifarios.abm.descripcion', defaultMessage: 'Descripción' }),
                        false,
                        DESCRIPCION_MAX
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="la la-truck"></i>
                      {intl.formatMessage({ id: 'inventarioTarifarios.abm.header_proveedores', defaultMessage: 'Proveedores asociados' })}
                    </h4>
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group row">
                          <label className="col-md-2 label-control col-form-label" htmlFor="proveedores">
                            {intl.formatMessage({ id: 'inventarioTarifarios.abm.proveedores', defaultMessage: 'Proveedores' })}:
                          </label>
                          <div className="col-md-10">
                            {isView ? (
                              <div className="form-control-static col-form-label form-value">
                                {formData.proveedores.length > 0
                                  ? formData.proveedores.map((p) => p.label).join(', ')
                                  : intl.formatMessage({ id: 'inventarioTarifarios.abm.sin_proveedores', defaultMessage: 'Sin proveedores' })}
                              </div>
                            ) : (
                              <Autocomplete
                                multiple
                                size="small"
                                id="proveedores"
                                options={opcionesProveedores}
                                value={formData.proveedores}
                                getOptionLabel={(option) => (option ? option.label : '')}
                                getOptionSelected={(option, value) => option.value === value.value}
                                onChange={(_, value) => handleInputChange('proveedores', value || [])}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder={intl.formatMessage({
                                      id: 'inventarioTarifarios.abm.proveedores_placeholder',
                                      defaultMessage: 'Seleccione uno o más proveedores...',
                                    })}
                                  />
                                )}
                              />
                            )}
                            <small className="text-muted">
                              <FormattedMessage
                                id="inventarioTarifarios.abm.ayuda_proveedores"
                                defaultMessage="Un tarifario puede estar asociado a varios proveedores. Un proveedor puede tener un solo tarifario: si ya tiene otro, se pedirá confirmación para cambiarlo."
                              />
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <h4 className="form-section">
                      <i className="la la-list"></i>
                      {intl.formatMessage({ id: 'inventarioTarifarios.abm.header_items', defaultMessage: 'Ítems del Catálogo' })}
                    </h4>
                    {props.action === 'ADD' ? (
                      <p className="text-muted mb-0">
                        <FormattedMessage
                          id="inventarioTarifarios.abm.items_despues_de_guardar"
                          defaultMessage="Guarde el tarifario para poder asignarle ítems: se asignan importando la planilla de ítems del Catálogo."
                        />
                      </p>
                    ) : (
                      formData.id > 0 && (
                        <InventarioTarifariosItems
                          tarifarioId={formData.id}
                          tarifarioNombre={formData.nombre}
                          isView={isView}
                          puedeEditar={Security.hasPermission('TARIFARIOS_MODIFICAR')}
                          cambios={cambiosItems}
                          onCambio={handleCambioItem}
                        />
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="card pull-up">
                <div className="card-content">
                  <div className="card-body">
                    <div className="text-cd text-right">
                      {!isView && (
                        <button onClick={handleSubmit} className="btn btn-primary mr-1">
                          <i className="fa fa-check-circle"></i>{' '}
                          {intl.formatMessage({ id: 'inventarioTarifarios.abm.guardar', defaultMessage: 'Guardar' })}
                        </button>
                      )}
                      <button type="button" className="btn btn-danger" onClick={() => exit()}>
                        <i className="fa fa-times-circle"></i>{' '}
                        {isView
                          ? intl.formatMessage({ id: 'inventarioTarifarios.abm.volver', defaultMessage: 'Volver' })
                          : intl.formatMessage({ id: 'inventarioTarifarios.abm.cancelar', defaultMessage: 'Cancelar' })}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default InventarioTarifariosAbm;
